import React from 'react';
import useI18n from '@/hooks/useI18n';
import { formatCount } from '@/utils/numberUtils';
import type {
  AudienceShare,
  CampaignCreatorsReach,
} from '@/interfaces/campaignKpis';
import { rampByRank } from './dummyCampaignInsights';

type AudienceBand = AudienceShare;
type CreatorsReach = CampaignCreatorsReach;

/** Meta sends ISO country codes; show the name in the viewer's language. */
const countryName = (code: string, locale: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

/**
 * Share of the track the leading bar fills. Figma draws the top age band and
 * top country at just under half the track, not full width.
 */
const LEAD_BAR_PERCENT = 47;

/** Hour colour by rank: Figma pairs the runners-up (2nd–3rd, 4th–5th). */
const hourColor = (rank: number): string =>
  rampByRank(rank === 0 ? 0 : Math.ceil(rank / 2));

/**
 * Start and end hour ("19", "22") of the busiest three consecutive hours,
 * wrapping past midnight. Ties keep the earliest window.
 */
export const peakWindow = (
  hourly: number[],
): { start: string; end: string } => {
  const sumFrom = (h: number) =>
    hourly[h] + hourly[(h + 1) % 24] + hourly[(h + 2) % 24];
  let best = 0;
  for (let h = 1; h < 24; h++) if (sumFrom(h) > sumFrom(best)) best = h;
  const pad = (h: number) => String(h % 24).padStart(2, '0');
  return { start: pad(best), end: pad(best + 3) };
};

/** Rank of each value within the list, strongest first. */
const ranksOf = (values: number[]): number[] => {
  const order = [...values].sort((a, b) => b - a);
  return values.map((v) => order.indexOf(v));
};

const BarTrack: React.FC<{ percent: number; color: string }> = ({
  percent,
  color,
}) => (
  <div className="flex h-[8px] min-w-0 flex-1 items-start overflow-hidden rounded-[5px] bg-white">
    <div
      className="h-full rounded-[5px]"
      style={{ width: `${percent}%`, backgroundColor: color }}
    />
  </div>
);

const AgeColumn: React.FC<{ bands: AudienceBand[] }> = ({ bands }) => {
  const { t } = useI18n();
  const max = Math.max(...bands.map((b) => b.percent), 1);
  const core = bands.reduce(
    (a, b) => (b.percent > a.percent ? b : a),
    bands[0],
  );

  return (
    <div
      data-testid="reach-age"
      className="flex min-w-0 flex-1 flex-col items-start gap-6 border-[#EAEAEA] p-6 lg:border-r"
    >
      <div className="flex w-full items-center justify-between whitespace-nowrap text-xs text-[#4D494F]">
        <p className="font-header font-extrabold">{t('common.age')}</p>
        <p data-testid="reach-age-core-band" className="font-header font-bold">
          {t('common.ageCoreBand', { band: core.label.replace('-', '–') })}
        </p>
      </div>
      <div className="flex w-full flex-col items-start gap-3">
        {bands.map((band) => {
          const ratio = band.percent / max;
          const opacity =
            ratio >= 0.9 ? 1 : ratio >= 0.5 ? 0.6 : ratio >= 0.2 ? 0.4 : 0.2;
          const isCore = band.label === core.label;
          return (
            <div
              key={band.label}
              data-testid="reach-age-band"
              className="flex w-full items-center justify-between"
            >
              <p className="w-[50px] shrink-0 font-header text-sm font-semibold text-[#4D494F]">
                {band.label.replace('-', '–')}
              </p>
              <BarTrack
                percent={(band.percent / max) * LEAD_BAR_PERCENT}
                color={`rgba(0, 170, 116, ${opacity})`}
              />
              <p
                className={`w-[36px] shrink-0 text-right font-header text-sm ${
                  isCore
                    ? 'font-extrabold text-[#1F2024]'
                    : 'font-semibold text-[#4D494F]'
                }`}
              >
                {band.percent}%
              </p>
            </div>
          );
        })}
      </div>
      <p className="w-full font-header text-xs font-medium leading-[1.4] text-[#4D4D4D]">
        {t('common.ageBandsNote')}
      </p>
    </div>
  );
};

const GenderAndHoursColumn: React.FC<{
  gender: CreatorsReach['gender'];
  hourlyActivity: number[] | null;
}> = ({ gender, hourlyActivity }) => {
  const { t } = useI18n();

  const legend = [
    {
      key: 'women',
      label: t('common.women'),
      value: gender.women,
      color: '#D5548E',
    },
    { key: 'men', label: t('common.men'), value: gender.men, color: '#2F6FEB' },
    {
      key: 'unspecified',
      label: t('common.unspecified'),
      value: gender.unspecified,
      color: '#D1D6E0',
    },
  ];

  return (
    <div
      data-testid="reach-gender"
      className="flex min-w-0 flex-1 flex-col items-start gap-4 border-[#EAEAEA] p-6 lg:border-r"
    >
      <div className="flex w-full flex-col items-start gap-4">
        <p className="font-header text-xs font-extrabold text-[#4D494F]">
          {t('common.gender')}
        </p>
        <div className="flex h-[8px] w-full items-start gap-0.5 overflow-hidden rounded-md">
          {legend.map((item) => (
            <div
              key={item.key}
              className="h-full"
              style={{ width: `${item.value}%`, backgroundColor: item.color }}
            />
          ))}
        </div>
        <div className="flex w-full flex-col items-start gap-2.5">
          {legend.map((item) => (
            <div
              key={item.key}
              data-testid={`reach-gender-${item.key}`}
              className="flex w-full items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-2.5 rounded-[3px]"
                  style={{ backgroundColor: item.color }}
                />
                <p className="font-header text-sm font-bold text-[#1F2024]">
                  {item.label}
                </p>
              </div>
              <p className="font-header text-sm font-extrabold text-[#1F2024]">
                {item.value}%
              </p>
            </div>
          ))}
        </div>
      </div>

      {hourlyActivity && <OnlineHours hourlyActivity={hourlyActivity} />}
    </div>
  );
};

/** Hidden when Meta reports no online hours — it often returns none. */
const OnlineHours: React.FC<{ hourlyActivity: number[] }> = ({
  hourlyActivity,
}) => {
  const { t } = useI18n();
  // Scaled from the quietest hour, not from zero: Meta's hourly counts are
  // often flat (800–1,200), which would draw 24 near-equal bars.
  const low = Math.min(...hourlyActivity);
  const span = Math.max(...hourlyActivity) - low || 1;
  const ranks = ranksOf(hourlyActivity);

  return (
    <>
      <div className="h-px w-full bg-[#EAEAEA]" />

      <div
        data-testid="reach-online-hours"
        className="flex w-full flex-col items-start gap-3"
      >
        <p className="font-header text-xs font-extrabold text-[#4D494F]">
          {t('common.whenTheyAreOnline')}
        </p>
        <div className="flex w-full flex-col items-start gap-1.5">
          <div className="flex h-[50px] w-full items-end justify-center gap-1">
            {hourlyActivity.map((value, hour) => (
              <div
                key={hour}
                className="w-[10px] shrink-0 rounded-t-[100px]"
                style={{
                  height: `${4 + ((value - low) / span) * 46}px`,
                  backgroundColor: hourColor(ranks[hour]),
                }}
              />
            ))}
          </div>
          <div className="flex w-full items-center justify-between font-header text-[11px] font-medium text-[#4D494F]">
            {['00', '06', '12', '18', '23'].map((label, i, all) => (
              <p
                key={label}
                className={`w-5 ${
                  i === 0
                    ? ''
                    : i === all.length - 1
                      ? 'text-right'
                      : 'text-center'
                }`}
              >
                {label}
              </p>
            ))}
          </div>
        </div>
        <p className="w-full font-header text-xs font-medium leading-[1.4] text-[#4D4D4D]">
          {t('common.peakHoursNote', peakWindow(hourlyActivity))}
        </p>
      </div>
    </>
  );
};

const GeographyColumn: React.FC<{
  countries: AudienceBand[];
  cities: AudienceBand[];
}> = ({ countries, cities }) => {
  const { t, currentLanguage } = useI18n();
  const max = Math.max(...countries.map((c) => c.percent), 1);

  return (
    <div
      data-testid="reach-geography"
      className="flex min-w-0 flex-1 flex-col items-start gap-4 p-6"
    >
      <div className="flex w-full flex-col items-start gap-4">
        <p className="font-header text-xs font-extrabold text-[#4D494F]">
          {t('common.topFiveCountries')}
        </p>
        <div className="flex w-full flex-col items-start gap-3">
          {countries.map((country, index) => (
            <div
              key={country.label}
              data-testid="reach-country"
              className="flex w-full items-center justify-between"
            >
              <p className="w-[110px] shrink-0 truncate font-header text-sm font-semibold text-[#4D494F]">
                {countryName(country.label, currentLanguage)}
              </p>
              <BarTrack
                percent={(country.percent / max) * LEAD_BAR_PERCENT}
                color={rampByRank(index)}
              />
              <p className="w-8 shrink-0 text-right font-header text-sm font-bold text-[#1F2024]">
                {country.percent}%
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="h-px w-full bg-[#EAEAEA]" />

      <div className="flex w-full flex-col items-start gap-4">
        <p className="font-header text-xs font-extrabold uppercase text-[#4D494F]">
          {t('common.topFiveCities')}
        </p>
        <div className="flex w-full flex-wrap content-start items-start gap-2 text-[13px]">
          {cities.map((city) => (
            <div
              key={city.label}
              data-testid="reach-city"
              className="flex shrink-0 gap-1 rounded-full bg-white px-3 py-1.5"
            >
              <p className="font-header font-bold text-[#1F2024]">
                {city.label}
              </p>
              <p className="font-header font-semibold text-[#62656E]">
                {city.percent}%
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface CreatorsReachPanelProps {
  reach: CreatorsReach;
}

/** Pooled Instagram audience of the creators who posted for the brand. */
const CreatorsReachPanel: React.FC<CreatorsReachPanelProps> = ({ reach }) => {
  const { t } = useI18n();

  return (
    <div data-testid="creators-reach-panel" className="w-full">
      <div className="flex w-full flex-col items-start gap-2 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <p className="font-header text-base font-semibold leading-6 tracking-[0.096px] text-[#172B4D]">
          {t('common.knowYourCreatorsReach')}
        </p>
        <p
          data-testid="reach-pooled-audience"
          className="font-header text-xs font-normal leading-4 text-[#9B919D]"
        >
          {t('common.pooledInstagramAudience', { count: reach.creatorCount })}
        </p>
      </div>

      <div className="flex w-full flex-col items-start rounded-3xl border border-[#E5E7EB] bg-[#F9F9F9]">
        <div className="flex w-full flex-col items-start justify-between gap-2 px-5 py-5 sm:flex-row sm:items-center">
          <div className="flex flex-col items-start gap-1.5">
            <p className="font-header text-xs font-normal leading-4 text-[#9B919D]">
              {t('common.combinedFollowerReach')}
            </p>
            <p
              data-testid="reach-combined-followers"
              className="font-header text-2xl font-normal leading-8 text-[#191B1C]"
            >
              {formatCount(reach.combinedFollowerReach)}
            </p>
          </div>
          <p
            data-testid="reach-last-synced"
            className="font-header text-xs font-normal leading-4 text-[#9B919D] sm:max-w-[240px] sm:text-right"
          >
            {t('common.lastSyncedViaInstagram')}
          </p>
        </div>

        <div className="w-full rounded-b-3xl border-t border-[#E4E4E7] bg-white py-5">
          <div className="flex w-full flex-col items-stretch lg:flex-row lg:items-start">
            <AgeColumn bands={reach.ageBands} />
            <GenderAndHoursColumn
              gender={reach.gender}
              hourlyActivity={reach.hourlyActivity}
            />
            <GeographyColumn
              countries={reach.topCountries}
              cities={reach.topCities}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorsReachPanel;
